import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { 
  Flower2, 
  Globe,
  ArrowLeft,
  CheckCircle2,
  Send
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DomainRequest = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    brand_name: "",
    preferred_domain: "",
    email: user?.email || "",
    notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.brand_name || !formData.preferred_domain || !formData.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/store/domain-request`, formData);
      setIsSubmitted(true);
      toast.success("Domain request submitted successfully!");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#FDFCF8]">
        <nav className="nav-glass sticky top-0 z-50 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/store")}
              className="flex items-center gap-2 text-[#2C332E] hover:bg-[#F5F0EB]"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Store
            </Button>
            <div className="flex items-center">
              <span className="text-xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Bloomwright Studio
              </span>
            </div>
            <div className="w-32" />
          </div>
        </nav>

        <main className="max-w-lg mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-[#8DA399]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#8DA399]" />
          </div>
          <h1 className="text-3xl font-semibold text-[#2C332E] mb-4">
            Request Submitted!
          </h1>
          <p className="text-[#78716C] mb-8">
            We've received your domain registration request. Our team will check availability and contact you within 24-48 hours.
          </p>
          <Button 
            onClick={() => navigate("/store")}
            className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-8 py-3"
          >
            Return to Store
          </Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Navigation */}
      <nav className="nav-glass sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/store")}
            className="flex items-center gap-2 text-[#2C332E] hover:bg-[#F5F0EB]"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Store
          </Button>
          <div className="flex items-center">
            <span className="text-xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Bloomwright Studio
            </span>
          </div>
          <div className="w-32" />
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#8DA399]/10 flex items-center justify-center mx-auto mb-4">
            <Globe className="w-8 h-8 text-[#8DA399]" />
          </div>
          <h1 className="text-3xl font-semibold text-[#2C332E] mb-2">
            Domain Registration Service
          </h1>
          <p className="text-[#78716C]">
            Let us help you secure the perfect domain for your brand
          </p>
        </div>

        <Card className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft">
          <CardHeader>
            <CardTitle className="text-xl text-[#2C332E]">Request Form</CardTitle>
            <CardDescription>
              Fill out the details below and we'll handle the domain registration for you. Service fee: $29
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="brand_name">Brand Name *</Label>
                <Input
                  id="brand_name"
                  placeholder="e.g., Bloom Botanica"
                  value={formData.brand_name}
                  onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                  className="bg-white/50 border-[#E6E1DB] focus:border-[#8DA399] rounded-xl"
                  data-testid="brand-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="preferred_domain">Preferred Domain *</Label>
                <Input
                  id="preferred_domain"
                  placeholder="e.g., bloombotanica.com"
                  value={formData.preferred_domain}
                  onChange={(e) => setFormData({ ...formData, preferred_domain: e.target.value })}
                  className="bg-white/50 border-[#E6E1DB] focus:border-[#8DA399] rounded-xl"
                  data-testid="domain-input"
                />
                <p className="text-xs text-[#78716C]">
                  We'll check availability and suggest alternatives if needed
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Contact Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-white/50 border-[#E6E1DB] focus:border-[#8DA399] rounded-xl"
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Any specific requirements or alternative domain ideas..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-white/50 border-[#E6E1DB] focus:border-[#8DA399] rounded-xl min-h-[100px]"
                  data-testid="notes-input"
                />
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full py-3"
                data-testid="submit-domain-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner w-5 h-5 mr-2 border-2 border-white border-t-transparent"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-8 p-6 bg-[#F5F0EB] rounded-xl">
          <h3 className="font-semibold text-[#2C332E] mb-3">How it works:</h3>
          <ol className="space-y-2 text-sm text-[#78716C]">
            <li className="flex gap-2">
              <span className="font-semibold text-[#8DA399]">1.</span>
              Submit your preferred domain name
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-[#8DA399]">2.</span>
              We check availability and contact you
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-[#8DA399]">3.</span>
              Once confirmed, we register the domain for you
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-[#8DA399]">4.</span>
              Domain transferred to your ownership
            </li>
          </ol>
        </div>
      </main>
    </div>
  );
};

export default DomainRequest;
